// Export module system core
export * from './types';
export { moduleRegistry } from './moduleRegistry';
export { EXTENSION_POINTS } from './extensionPoints';
export { ModuleProvider, useModules, useModule, useModuleExtension } from '@/contexts/ModuleContext';

// Import for internal use
import { moduleRegistry } from './moduleRegistry';
import i18n from '@/i18n';
import type { ModuleDefinition } from './types';

/**
 * Automatically discover and load modules at runtime
 * Uses Vite's import.meta.glob to discover all module index files
 * This is evaluated at build time but allows dynamic loading at runtime
 */
async function discoverModules(): Promise<Array<{ path: string; loader: () => Promise<any> }>> {
  const moduleLoaders: Array<{ path: string; loader: () => Promise<any> }> = [];
  
  try {
    // Use Vite's import.meta.glob to discover all module index files
    // This pattern matches: ./moduleName/index.ts or ./moduleName/index.tsx
    // The glob is evaluated at build time, but we can iterate over it at runtime
    // IMPORTANT: import.meta.glob returns functions, not paths!
    const modules = import.meta.glob('./*/index.{ts,tsx}', { eager: false });
    
    // Extract module paths and their loader functions
    for (const path in modules) {
      // Path format: './jobModule/index.tsx'
      // modules[path] is a function that returns a Promise
      moduleLoaders.push({
        path,
        loader: modules[path] as () => Promise<any>
      });
    }
    
    // Sort for consistent loading order
    moduleLoaders.sort((a, b) => a.path.localeCompare(b.path));
  } catch (error) {
    console.warn('Error discovering modules at runtime:', error);
    // Fallback: return empty array, modules can still be manually registered
  }
  
  return moduleLoaders;
}

/**
 * Register a module's translations with i18n.
 * Uses the module's `id` as the namespace so components can access
 * translations via `useTranslation('module-id')`.
 */
function registerModuleTranslations(definition: ModuleDefinition): void {
  if (!definition.translations) return;

  for (const [lang, resources] of Object.entries(definition.translations)) {
    // addResourceBundle(lng, ns, resources, deep, overwrite)
    i18n.addResourceBundle(lang, definition.id, resources, true, false);
  }

  console.log(`Registered translations for module: ${definition.id}`);
}

/**
 * Register all modules automatically
 * Modules are discovered at runtime and loaded at application startup
 */
export async function registerModules() {
  try {
    // Automatically discover modules (returns loaders, not paths)
    const moduleLoaders = await discoverModules();
    
    if (moduleLoaders.length === 0) {
      console.warn('No modules discovered. Make sure module directories contain index.ts or index.tsx files.');
      return;
    }
    
    // Load all discovered modules using the loader functions from import.meta.glob
    const loadPromises = moduleLoaders.map(async ({ path, loader }) => {
      try {
        // Use the loader function directly (this is what import.meta.glob provides)
        // This ensures Vite properly bundles the module at build time
        const module = await loader();
        
        const definition = module.default?.default || module.default || module.module;

        if (!definition?.id || !definition?.name || !definition?.version) {
          throw new Error(
            `Module at ${path} must export a ModuleDefinition with id, name, and version`,
          );
        }
        
        // Register the module directly (we already have the definition loaded)
        moduleRegistry.register(definition);

        // Register module translations with i18n (if provided)
        registerModuleTranslations(definition);
      } catch (error) {
        console.error(`Failed to load module from ${path}:`, error);
        throw error;
      }
    });
    
    await Promise.allSettled(loadPromises);
    
    console.log(`Successfully registered ${moduleLoaders.length} module(s)`);
  } catch (error) {
    console.error('Failed to register modules:', error);
    throw error;
  }
}
